import React, { useState, useEffect } from 'react';
import { MealItem, mealAPI } from '../api';
import { utcToLocalInput, localInputToUtc } from '../utils/timezone';

interface MealItemEditorProps {
  item: MealItem;
  onUpdate: (updatedItem: MealItem) => void;
  onDelete: (itemId: number) => void;
  onCancel: () => void;
}

const MealItemEditor: React.FC<MealItemEditorProps> = ({ item, onUpdate, onDelete, onCancel }) => {
  const [foodName, setFoodName] = useState(item.food_name || '');
  const [qty, setQty] = useState(item.qty?.toString() || '');
  const [unit, setUnit] = useState(item.unit || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [carbs, setCarbs] = useState(item.macros.carbs.toString());
  const [protein, setProtein] = useState(item.macros.protein.toString());
  const [fat, setFat] = useState(item.macros.fat.toString());
  const [calories, setCalories] = useState(item.macros.calories.toString());
  const [mealTime, setMealTime] = useState(() => {
    // Convert UTC meal_time to local datetime-local format
    return utcToLocalInput(item.meal_time);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Store original values to calculate proportional changes
  const originalQty = item.qty || 1;
  const originalMacros = {
    carbs: item.macros.carbs,
    protein: item.macros.protein,
    fat: item.macros.fat,
    calories: item.macros.calories
  };

  // Function to round to 1 decimal place
  const roundToOneDecimal = (num: number): number => {
    return Math.round(num * 10) / 10;
  };

  // Handle quantity change and update macros proportionally
  const handleQtyChange = (newQtyStr: string) => {
    setQty(newQtyStr);
    
    const newQty = parseFloat(newQtyStr) || 0;
    if (newQty > 0) {
      const multiplier = newQty / originalQty;
      
      // Update macros proportionally
      setCarbs(roundToOneDecimal(originalMacros.carbs * multiplier).toString());
      setProtein(roundToOneDecimal(originalMacros.protein * multiplier).toString());
      setFat(roundToOneDecimal(originalMacros.fat * multiplier).toString());
      setCalories(Math.round(originalMacros.calories * multiplier).toString()); // Calories should be whole numbers
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const updates = {
        food_name: foodName.trim(),
        qty: qty.trim() ? parseFloat(qty) : undefined,
        unit: unit.trim() || undefined,
        notes: notes.trim() || undefined,
        carbs: parseFloat(carbs),
        protein: parseFloat(protein),
        fat: parseFloat(fat),
        calories: parseFloat(calories),
        meal_time: localInputToUtc(mealTime),
      };

      const response = await mealAPI.updateMealItem(item.id!, updates);
      
      if (response.success && response.item) {
        // Convert backend format to frontend format
        const updatedItem: MealItem = {
          ...response.item,
          macros: {
            carbs: (response.item as any).carbs,
            protein: (response.item as any).protein,
            fat: (response.item as any).fat,
            calories: (response.item as any).calories,
          }
        };
        onUpdate(updatedItem);
      } else {
        setError(response.error || 'Failed to update item');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await mealAPI.deleteMealItem(item.id!);
      
      if (response.success) {
        onDelete(item.id!);
      } else {
        setError(response.error || 'Failed to delete item');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete item');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Edit Food Item</h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Food Name *
              </label>
              <input
                type="text"
                value={foodName}
                onChange={(e) => setFoodName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., grilled chicken breast"
              />
            </div>

            {/* Meal Date & Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date & Time
              </label>
              <input
                type="datetime-local"
                value={mealTime}
                onChange={(e) => setMealTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={qty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <input
                  type="text"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="cup, slice, g"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="brand, preparation, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Macros (override automatic calculation)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-blue-600 mb-1">
                    Carbs (g)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={carbs}
                    onChange={(e) => setCarbs(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-green-600 mb-1">
                    Protein (g)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={protein}
                    onChange={(e) => setProtein(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-amber-600 mb-1">
                    Fat (g)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={fat}
                    onChange={(e) => setFat(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-purple-600 mb-1">
                    Calories
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={calories}
                    onChange={(e) => setCalories(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleDelete}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
              
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={isLoading || !foodName.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>

          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">
              <strong>Note:</strong> Editing food name or quantity will auto-calculate macros via AI. 
              Manual macro values above will override the AI calculation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealItemEditor;